/**
 * Site-wide SEO configuration for GeoVera (geovera.in).
 * Used by usePageSeo / SeoRoute for per-path titles, descriptions, and robots.
 */
import { GEOVERA_WEB_URL } from '../config/api';

export const SITE_NAME = 'GeoVera';
export const SITE_URL = GEOVERA_WEB_URL || 'https://geovera.in';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-preview.svg`;
export const TWITTER_HANDLE = '@geovera';
export const DEFAULT_LOCALE = 'en_IN';
export const THEME_COLOR = '#0B132B';

export interface PageSeo {
  /** Document title (full string; use with site name suffix where appropriate) */
  title: string;
  description: string;
  /** Path starting with / — used for canonical */
  path: string;
  /** keywords meta (optional, low weight but useful for some crawlers) */
  keywords?: string;
  /** noindex,nofollow for private / auth surfaces */
  robots?: string;
  /** Open Graph type */
  ogType?: 'website' | 'article' | 'product';
  /** Override OG image */
  ogImage?: string;
  /** JSON-LD graph extras for this page (merged with site defaults) */
  schemaType?:
    | 'WebPage'
    | 'SoftwareApplication'
    | 'Product'
    | 'FAQPage'
    | 'TechArticle'
    | 'CollectionPage';
}

const withBrand = (title: string) =>
  title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

/** Public + product marketing pages */
export const PAGE_SEO: Record<string, PageSeo> = {
  '/': {
    path: '/',
    title: withBrand('WebGIS Platform, DIGIPIN & Digital Property Maps'),
    description:
      'GeoVera is an AI-powered WebGIS platform for land measurement, DIGIPIN 4m geo-grid encoding, QGIS plugins (DIGIPIN Encoder & ClipToCAD), and shareable digital property cards on geovera.in.',
    keywords:
      'WebGIS, DIGIPIN, digital property card, land survey, QGIS plugin, ClipToCAD, spatial analysis, GeoJSON, India geo-grid, cadastral, GeoVera',
    ogType: 'website',
    schemaType: 'WebPage',
  },
  '/pricing': {
    path: '/pricing',
    title: withBrand('Pricing — Community Free Forever, Studio & Enterprise'),
    description:
      'GeoVera Community is full WebGIS free forever for every human. Studio for commercial DIGIPIN API scale. Enterprise for orgs and design partners. Not an official India Post product.',
    keywords:
      'GeoVera pricing, free WebGIS, DIGIPIN API, Community Studio Enterprise, India spatial platform',
    ogType: 'website',
    schemaType: 'WebPage',
  },
  '/map': {
    path: '/map',
    title: withBrand('Free WebGIS Map — Open Without Signup'),
    description:
      'Open GeoVera free map workspace: draw boundaries, measure, DIGIPIN tools, and share maps. Full product free forever with fair-use limits.',
    keywords: 'free WebGIS, open map, DIGIPIN map, land survey online, GeoVera map',
    ogType: 'website',
    schemaType: 'SoftwareApplication',
  },
  '/qgis-plugins': {
    path: '/qgis-plugins',
    title: withBrand('QGIS Plugins — DIGIPIN Encoder & ClipToCAD'),
    description:
      'Install GeoVera QGIS plugins: DIGIPIN Encoder Pro for 4m national geo-pins and ClipToCAD for one-click AutoCAD clipboard transfer from QGIS.',
    keywords:
      'QGIS plugins, DIGIPIN Encoder Pro, ClipToCAD, QGIS AutoCAD, India Post DIGIPIN, QGIS 3, QGIS 4',
    ogType: 'website',
    schemaType: 'CollectionPage',
  },
  '/digipin-encoder': {
    path: '/digipin-encoder',
    title: withBrand('DIGIPIN Encoder Pro for QGIS — 4m Geo-Grid'),
    description:
      'Official-style DIGIPIN encoding in QGIS: map click encode, decode-to-zoom, batch attributes, online API keys, and offline hybrid mode at ~4m resolution.',
    keywords:
      'DIGIPIN, DIGIPIN Encoder, India Post geo-grid, QGIS DIGIPIN, 4m grid, spatial address',
    ogType: 'product',
    schemaType: 'SoftwareApplication',
  },
  '/digipin-encoder/issues': {
    path: '/digipin-encoder/issues',
    title: withBrand('DIGIPIN Encoder Issues & Feedback'),
    description:
      'Report bugs and feature ideas for the DIGIPIN Encoder Pro QGIS plugin. Public issue tracker for GeoVera users.',
    keywords: 'DIGIPIN Encoder issues, QGIS plugin support, GeoVera feedback',
    ogType: 'website',
    schemaType: 'WebPage',
  },
  '/cliptocad': {
    path: '/cliptocad',
    title: withBrand('ClipToCAD 2.0.0 — QGIS to AutoCAD Clipboard Bridge'),
    description:
      'Copy selected QGIS features to AutoCAD as _PLINE, _POINT, and _TEXT. ClipToCAD 2.0.0 supports QGIS 3.16–4.x, multi-layer selections, unit presets, and auto parcel-ID labels.',
    keywords:
      'ClipToCAD, QGIS AutoCAD, CAD clipboard, polyline paste AutoCAD, QGIS plugin CAD, AutoCAD LT, cadastral, QGIS 4',
    ogType: 'product',
    schemaType: 'SoftwareApplication',
  },
  '/cliptocad/issues': {
    path: '/cliptocad/issues',
    title: withBrand('ClipToCAD Support, FAQ & Issues'),
    description:
      'ClipToCAD 2.0.0 FAQ: selection-only copy, Real vs Local mode, parcel-ID labels, QGIS 3.16–4.x, install folder name, and GitHub issue reporting.',
    keywords: 'ClipToCAD FAQ, ClipToCAD support, QGIS CAD issues, AutoCAD paste, QGIS 4 plugin',
    ogType: 'website',
    schemaType: 'FAQPage',
  },
  '/login': {
    path: '/login',
    title: withBrand('Sign In'),
    description:
      'Sign in to GeoVera with Google OAuth to access WebGIS workspace, DIGIPIN API keys, and cloud projects.',
    robots: 'noindex, follow',
    ogType: 'website',
    schemaType: 'WebPage',
  },
  '/signup': {
    path: '/signup',
    title: withBrand('Create Account'),
    description:
      'Create a GeoVera workspace with Google sign-in. Automatic account provisioning for WebGIS and DIGIPIN tools.',
    robots: 'noindex, follow',
    ogType: 'website',
    schemaType: 'WebPage',
  },
  '/terms': {
    path: '/terms',
    title: withBrand('Terms of Service'),
    description:
      'Terms of Service for GeoVera WebGIS platform, DIGIPIN APIs, and QGIS plugins at geovera.in.',
    keywords: 'GeoVera terms of service, legal',
    ogType: 'website',
    schemaType: 'WebPage',
  },
  '/privacy': {
    path: '/privacy',
    title: withBrand('Privacy Policy'),
    description:
      'Privacy Policy for GeoVera: how we handle Google OAuth data, spatial layers, API keys, and usage analytics.',
    keywords: 'GeoVera privacy policy, data protection',
    ogType: 'website',
    schemaType: 'WebPage',
  },
  '/dashboard': {
    path: '/dashboard',
    title: withBrand('WebGIS Workspace'),
    description: 'GeoVera interactive WebGIS dashboard — map layers, measure, draw, and analysis.',
    robots: 'noindex, nofollow',
    ogType: 'website',
  },
  '/profile': {
    path: '/profile',
    title: withBrand('Profile'),
    description: 'Manage your GeoVera account profile.',
    robots: 'noindex, nofollow',
  },
  '/profile/complete': {
    path: '/profile/complete',
    title: withBrand('Complete Profile'),
    description: 'Complete your GeoVera profile to continue.',
    robots: 'noindex, nofollow',
  },
  '/developers/digipin': {
    path: '/developers/digipin',
    title: withBrand('DIGIPIN Developer Portal & API Keys'),
    description:
      'Claim and manage DIGIPIN cloud API keys for high-throughput encoding in DIGIPIN Encoder Pro and custom integrations.',
    robots: 'noindex, nofollow',
  },
  '/account': {
    path: '/account',
    title: withBrand('Account — Projects & Fair-use'),
    description: 'Your GeoVera cloud projects, DIGIPIN status, and Community fair-use meters.',
    robots: 'noindex, nofollow',
  },
  '/admin': {
    path: '/admin',
    title: withBrand('Admin Hub'),
    description: 'Platform admin: users, force-unpublish, DIGIPIN keys.',
    robots: 'noindex, nofollow',
  },
  '/admin/api-keys': {
    path: '/admin/api-keys',
    title: withBrand('Admin — DIGIPIN API Keys'),
    description: 'Admin console for DIGIPIN API key approvals.',
    robots: 'noindex, nofollow',
  },
};

/** Fallback for public map share links and unknown paths */
export const DEFAULT_PAGE_SEO: PageSeo = {
  path: '/',
  title: withBrand('WebGIS Platform & Spatial Intelligence'),
  description:
    'GeoVera WebGIS: maps, DIGIPIN, QGIS plugins, and digital property cards for survey and land workflows.',
  keywords: 'GeoVera, WebGIS, DIGIPIN, QGIS',
  ogType: 'website',
  schemaType: 'WebPage',
};

export function resolvePageSeo(pathname: string): PageSeo {
  const path = pathname.replace(/\/+$/, '') || '/';

  // Public share maps
  if (path.startsWith('/m/')) {
    return {
      path,
      title: withBrand('Shared Map'),
      description:
        'View a shared GeoVera digital property map with satellite context and parcel details.',
      robots: 'index, follow',
      ogType: 'website',
      schemaType: 'WebPage',
    };
  }

  // Auth aliases
  if (['/sign-in', '/signin', '/sign-up'].includes(path)) {
    return path.includes('up') ? PAGE_SEO['/signup'] : PAGE_SEO['/login'];
  }

  return PAGE_SEO[path] ?? { ...DEFAULT_PAGE_SEO, path };
}

/** Absolute URL helper */
export function absoluteUrl(path: string): string {
  if (path.startsWith('http')) return path;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${p === '/' ? '/' : p}`;
}

/** Indexable public paths for sitemap generation / reference */
export const SITEMAP_PATHS: { path: string; changefreq: string; priority: number }[] = [
  { path: '/', changefreq: 'weekly', priority: 1.0 },
  { path: '/map', changefreq: 'weekly', priority: 0.95 },
  { path: '/pricing', changefreq: 'weekly', priority: 0.9 },
  { path: '/qgis-plugins', changefreq: 'weekly', priority: 0.9 },
  { path: '/digipin-encoder', changefreq: 'weekly', priority: 0.9 },
  { path: '/cliptocad', changefreq: 'weekly', priority: 0.9 },
  { path: '/digipin-encoder/issues', changefreq: 'monthly', priority: 0.5 },
  { path: '/cliptocad/issues', changefreq: 'monthly', priority: 0.5 },
  { path: '/terms', changefreq: 'yearly', priority: 0.3 },
  { path: '/privacy', changefreq: 'yearly', priority: 0.3 },
  { path: '/login', changefreq: 'monthly', priority: 0.4 },
];
